import { useState, useEffect, useMemo }  from 'react';
import { create } from 'zustand';
import { toast } from 'react-toastify';
import useEmpresas from './useEmpresas';
import {
    createVinculacion,
    fetchGeneros,
    fetchTiposDocumento,
    fetchNaturalezas,
    fetchPaises,
    fetchMunicipios,
    fetchLicencias,
    fetchTiposVehiculos,
    fetchEmpresasSeguros,
    fetchMarcas,
    fetchCombustibles,
    fetchLineas,
    fetchCarrocerias,
    fetchConfiguraciones
} from '../api/vinculacionApi';
import {
    terceroBaseSchema,
    documentoSchema,
    conductorSchema,
    vehiculoSchema
} from './validators';

import '../../../css/Vinculacion.css';

// -----------------------------------------------------------------------------
// Zustand store para datos maestros 
// -----------------------------------------------------------------------------
const useVinculacionStore = create((set) => ({
    generos: [],
    tiposDocumento: [],
    naturalezas: [],
    paises: [],
    municipios: [],
    licencias: [],
    tiposVehiculos: [],
    empresasSeguros: [],
    marcas: [],
    combustibles: [],
    carrocerias: [],
    configuraciones: [],
    lineas: [],

    // Carga todos los catálogos de golpe
    fetchMasterData: async () => {
        try {
            const [
                generos,
                tiposDocumento,
                naturalezas,
                paises,
                municipios,
                licencias,
                tiposVehiculos,
                empresasSeguros,
                marcas,
                combustibles,
                carrocerias,
                configuraciones
            ] = await Promise.all([
                fetchGeneros(),
                fetchTiposDocumento(),
                fetchNaturalezas(),
                fetchPaises(),
                fetchMunicipios(),
                fetchLicencias(),
                fetchTiposVehiculos(),
                fetchEmpresasSeguros(),
                fetchMarcas(),
                fetchCombustibles(),
                fetchCarrocerias(),
                fetchConfiguraciones()
            ]);
            set({
                generos,
                tiposDocumento,
                naturalezas,
                paises,
                municipios,
                licencias,
                tiposVehiculos,
                empresasSeguros,
                marcas,
                combustibles,
                carrocerias,
                configuraciones,
                lineas: []
            });
        } catch {
            toast.error('Error al cargar datos.', { position: 'top-right' });
        }
    },

    // Carga sólo las líneas de la marca seleccionada
    fetchLineasByMarca: async (codigoMarca) => {
        if (!codigoMarca) {
            set({ lineas: [] });
            return;
        }
        try {
            const lineas = await fetchLineas({ codigoMarca });
            set({ lineas });
        } catch {
            toast.error('Error al cargar líneas de vehículos.', { position: 'top-right' });
        }
    }
}));

// -----------------------------------------------------------------------------
// Configuración de pasos del formulario (wizard)
// -----------------------------------------------------------------------------
const STEP_CONFIG = {
    1: { title: 'Vinculación de Propietario', icon: 'person' },
    2: { title: 'Vinculación de Tenedor', icon: 'person_outline' },
    3: { title: 'Vinculación de Conductor', icon: 'drive_eta' },
    4: { title: 'Vinculación de Vehículo', icon: 'directions_car' }
};

// -----------------------------------------------------------------------------
// Claves para sessionStorage, mapeadas a cada paso
// -----------------------------------------------------------------------------
const STORAGE_KEYS = ['propietario', 'tenedor', 'conductor', 'vehiculo'];

// -----------------------------------------------------------------------------
// Función para calcular el Dígito de Verificación 
// -----------------------------------------------------------------------------
const calculateDV = (pNit) => {
    const arrPesos = [];
    arrPesos[1] = 3;  arrPesos[2] = 7;  arrPesos[3] = 13; arrPesos[4] = 17; arrPesos[5] = 19;
    arrPesos[6] = 23; arrPesos[7] = 29; arrPesos[8] = 37; arrPesos[9] = 41; arrPesos[10] = 43;
    arrPesos[11] = 47; arrPesos[12] = 53; arrPesos[13] = 59; arrPesos[14] = 67; arrPesos[15] = 71;

    const nit = (pNit || '').toString();
    let peso = 0;
    for (let i = 0; i < nit.length; i++) {
        const dig = parseInt(nit.charAt(nit.length - 1 - i), 10);
        if (!Number.isNaN(dig)) peso += dig * arrPesos[i + 1];
    }
    let dv = peso % 11;
    if (dv >= 2) dv = 11 - dv;
    return dv.toString();
};

// -----------------------------------------------------------------------------
// Hook principal para gestionar el flujo de vinculación de terceros.
// Las llamadas a datos maestros sólo ocurren cuando la empresa existe.
// -----------------------------------------------------------------------------
export default function useVinculacionForm(tenant) {
    // -------------------------------------------------------------------------
    // Empresa: cargada con useEmpresas
    // -------------------------------------------------------------------------
    const {
        empresa,
        loading: empresaLoading,
        error: empresaError
    } = useEmpresas(tenant);
    const logoUrls = empresa?.logoUrls ?? [];
    const logoFallback = empresa?.logoFallback ?? '';
    const nameState = empresa?.nombre_empresa ?? '';
    const policyUrl = empresa?.tratamiento_datos || null;

    // -------------------------------------------------------------------------
    // Estados locales
    // -------------------------------------------------------------------------
    const [step, setStep]  = useState(1);
    const [modal, setModal] = useState({ 
        visible: false, 
        title: '', 
        mode: null,
        url: '' 
    });
    const [showMore, setShowMore] = useState(false);
    const [formData, setFormData] = useState({
        1: { naturaleza: 'N', digitoVerificacion: '' },
        2: { digitoVerificacion: '' },
        3: { digitoVerificacion: '' },
        4: {}
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // -------------------------------------------------------------------------
    // Conectar con el store de Zustand para datos maestros
    // -------------------------------------------------------------------------
    const generos  = useVinculacionStore(s => s.generos);
    const tiposDocumento = useVinculacionStore(s => s.tiposDocumento);
    const naturalezas = useVinculacionStore(s => s.naturalezas);
    const paises = useVinculacionStore(s => s.paises);
    const municipios = useVinculacionStore(s => s.municipios);
    const licencias = useVinculacionStore(s => s.licencias);
    const tiposVehiculos = useVinculacionStore(s => s.tiposVehiculos);
    const empresasSeguros = useVinculacionStore(s => s.empresasSeguros);
    const marcas = useVinculacionStore(s => s.marcas);
    const combustibles = useVinculacionStore(s => s.combustibles);
    const carrocerias = useVinculacionStore(s => s.carrocerias);
    const configuraciones = useVinculacionStore(s => s.configuraciones);
    const lineas = useVinculacionStore(s => s.lineas);
    const fetchMasterData = useVinculacionStore(s => s.fetchMasterData);
    const fetchLineasByMarca = useVinculacionStore(s => s.fetchLineasByMarca);

    const { title, icon } = useMemo(() => STEP_CONFIG[step], [step]);

    // -------------------------------------------------------------------------
    // Cargar datos maestros: solo cuando exista la empresa (no error, empresa != null)
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (empresa && !empresaLoading && !empresaError) {
            fetchMasterData();
        }
    }, [empresa, empresaLoading, empresaError, fetchMasterData]);

    // -------------------------------------------------------------------------
    // Cargar líneas vehículo en el paso 4, sólo si la empresa está cargada
    // -------------------------------------------------------------------------
    const marcaSeleccionada = formData[4]?.marca;
    useEffect(() => {
        if (empresa && step === 4 && marcaSeleccionada) {
            fetchLineasByMarca(marcaSeleccionada);
        }
    }, [empresa, step, marcaSeleccionada, fetchLineasByMarca]);

    // -------------------------------------------------------------------------
    // Inicializar formData desde sessionStorage (siempre)
    // -------------------------------------------------------------------------
    useEffect(() => {
        setFormData(prev => {
            const loaded = { ...prev };
            STORAGE_KEYS.forEach((key, idx) => {
                const raw = sessionStorage.getItem(key);
                loaded[idx + 1] = raw ? JSON.parse(raw) : {};
                // Asegurar campo DV en pasos 1,2,3
                if ([1,2,3].includes(idx + 1) && loaded[idx + 1].digitoVerificacion === undefined) {
                    loaded[idx + 1].digitoVerificacion = '';
                }
            });
            loaded[1].naturaleza = loaded[1].naturaleza || 'N';
            return loaded;
        });
    }, []);

    // -------------------------------------------------------------------------
    // Reset de scroll y errores al cambiar de paso
    // -------------------------------------------------------------------------
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setErrors({});
    }, [step]);

    // ------------------------------------------------------------------------
    // Cargar automáticamente data existente desde storage
    // ------------------------------------------------------------------------
    const currentDoc = useMemo(() => formData[step]?.numeroDocumento, [formData, step]);
    useEffect(() => {
        if (!currentDoc) return;

        // Paso 2: si coincide con propietario o conductor
        if (step === 2) {
            const rawP = sessionStorage.getItem('propietario');
            if (rawP) {
                const dataP = JSON.parse(rawP);
                if (dataP.numeroDocumento === currentDoc) {
                    setFormData(prev => ({ ...prev, 2: dataP }));
                }
            }
            const rawC = sessionStorage.getItem('conductor');
            if (rawC) {
                const dataC = JSON.parse(rawC);
                if (dataC.numeroDocumento === currentDoc) {
                    const { tipoLicencia, numeroLicencia, fechaVencimientoLicencia, anioInicioConductor, ...cleanC } = dataC;
                    setFormData(prev => ({ ...prev, 2: cleanC }));
                }
            }
        }

        // Paso 3: si coincide con tenedor o con propietario
        else if (step === 3) {
            const rawT = sessionStorage.getItem('tenedor');
            if (rawT) {
                const dataT = JSON.parse(rawT);
                if (dataT.numeroDocumento === currentDoc) {
                    setFormData(prev => ({ ...prev, 3: dataT }));
                }
            }
            const rawP = sessionStorage.getItem('propietario');
            if (rawP) {
                const dataP = JSON.parse(rawP);
                if (dataP.numeroDocumento === currentDoc) {
                    setFormData(prev => ({ ...prev, 3: dataP }));
                }
            }
            const rawC = sessionStorage.getItem('conductor');
            if (rawC) {
                const dataC = JSON.parse(rawC);
                if (dataC.numeroDocumento === currentDoc) {
                    setFormData(prev => ({ ...prev, 3: dataC }));
                }
            } 
        }
    }, [step, currentDoc]);

    // -------------------------------------------------------------------------
    // Modal handlers
    // -------------------------------------------------------------------------
    const openModal = ({ title, mode, url = '' }) => setModal({ visible: true, title, mode, url });
    const closeModal = () => setModal(m => ({ ...m, visible: false }));

    // -------------------------------------------------------------------------
    // Toggle sección "Mostrar más"
    // -------------------------------------------------------------------------
    const onToggleShowMore = () => setShowMore(m => !m);

    // -------------------------------------------------------------------------
    // onChange: actualiza estado, sessionStorage y valida campo concreto
    // -------------------------------------------------------------------------
    const onChange = (field, value) => {
        // Construir el objeto actualizado de este paso
        const prevData = formData[step] || {};
        const updatedData = { ...prevData, [field]: value };

        // Dígito de verificación
        if (field === 'tipoDocumento') {
            updatedData.naturaleza = ['N', 'NOP'].includes(value) ? 'J' : 'N';
            // Solo NIT ('N') o RUT ('R') llevan dígito de verificación
            const tp = value.toUpperCase();
            if ((tp === 'N' || tp === 'R') && prevData.numeroDocumento) {
                updatedData.digitoVerificacion = calculateDV(prevData.numeroDocumento);
            } else {
                updatedData.digitoVerificacion = '';
            }
        };
        if (field === 'numeroDocumento') {
            const tp = updatedData.tipoDocumento?.toUpperCase();
            if (tp === 'N' || tp === 'R') {
                updatedData.digitoVerificacion = calculateDV(value);
            } else {
                updatedData.digitoVerificacion = '';
            }
        };

        let schema;
        if (step < 4 && ['tipoDocumento', 'numeroDocumento', 'naturaleza'].includes(field)) {
            schema = documentoSchema;
        } else if (step < 3) {
            schema = terceroBaseSchema;
        } else if (step === 3) {
            schema = conductorSchema;
        } else {
            schema = vehiculoSchema;
        }

        // Validar sólo updatedData
        const result = schema.safeParse(updatedData);
        if (!result.success) {
            const fieldErrs = result.error.flatten().fieldErrors[field];
            setErrors(errs => ({ ...errs, [field]: fieldErrs }));
        } else {
            setErrors(errs => {
                const clean = { ...errs };
                delete clean[field];
                return clean;
            });
        }

        // Persistir en estado y sessionStorage
        setFormData(prev => {
            const all = { ...prev, [step]: updatedData };
            try {
                sessionStorage.setItem(STORAGE_KEYS[step - 1], JSON.stringify(updatedData));
            } catch (e) {
                console.error('Error guardando en sessionStorage', e);
            }
            return all;
        });
    };

    // -------------------------------------------------------------------------
    // handleBack: retroceder un paso y recargar del sessionStorage
    // -------------------------------------------------------------------------
    const handleBack = () => {
        const prev = step - 1;
        if (prev < 1) return;
        const raw  = sessionStorage.getItem(STORAGE_KEYS[prev - 1]);
        const data = raw ? JSON.parse(raw) : {};
        if (prev === 1) data.naturaleza = data.naturaleza || 'N';
        setFormData(f => ({ ...f, [prev]: data }));
        setStep(prev);
        setShowMore(false);
        setErrors({});
    };

    // -------------------------------------------------------------------------
    // Helper para guardar + propagar cambios solo al cambiar de paso
    // -----------------------------------------------------------------------------
    const saveAndPropagate = (key, data) => {
        // Clonar y limpiar razón social si ahora es Natural
        const saveData = { ...data };
        if (saveData.naturaleza === 'N') {
            delete saveData.razonSocial;
        }
        if (saveData.naturaleza === 'J') {
            delete saveData.primerNombre;
            delete saveData.primerApellido;
            delete saveData.segundoNombre;
            delete saveData.segundoApellido;
        }
        sessionStorage.setItem(key, JSON.stringify(saveData));
        const doc = data.numeroDocumento;
        STORAGE_KEYS.forEach(k => {
            if (k === key) return;
            const raw = sessionStorage.getItem(k);
            if (!raw) return;
            const other = JSON.parse(raw);
            if (other.numeroDocumento === doc) {
                const merged = { ...other, ...data };
                if (k !== 'conductor') {
                    delete merged.tipoLicencia;
                    delete merged.numeroLicencia;
                    delete merged.fechaVencimientoLicencia;
                    delete merged.anioInicioConductor;
                }
                if (merged.naturaleza === 'N') {
                    delete merged.razonSocial;
                }
                if (merged.naturaleza === 'J') {
                    delete merged.primerNombre;
                    delete merged.primerApellido;
                    delete merged.segundoNombre;
                    delete merged.segundoApellido;
                }
                sessionStorage.setItem(k, JSON.stringify(merged));
            }
        });
    };

    // -------------------------------------------------------------------------
    // createPayload: arma personas + vehículo (igual que antes)
    // -------------------------------------------------------------------------
    // Helper para mapear cada persona
    const mapPersona = (raw, rol) => {
        // Campos comunes
        const persona = {
            rol,
            tipo_documento: raw.tipoDocumento,
            documento: raw.numeroDocumento,
            digito_verificacion: raw.digitoVerificacion,
            naturaleza: raw.naturaleza,
            genero: raw.genero,
            primer_nombre: raw.primerNombre || '',
            segundo_nombre: raw.segundoNombre || '',
            primer_apellido: raw.primerApellido || '',
            segundo_apellido: raw.segundoApellido || '',
            razon_social: raw.razonSocial || '',
            pais: raw.pais,
            ciudad_residencia: raw.municipio,
            direccion: raw.direccion,
            celular: raw.celular,
            email: raw.email,
            politica_tratamiento: true,
            telefono_fijo: raw.telefonoFijo || '',
            fecha_nacimiento: raw.fechaNacimiento || '',
            ciudad_nacimiento: raw.lugarNacimiento || '',
            estado_civil: raw.estadoCivil || '',
            profesion: raw.profesion || '',
            empresa: raw.empresa || '',
            sitio_web: raw.sitioWeb || '',
            eps: raw.eps || '',
            arl: raw.arl || '',
            judicial: raw.judicial || '',
            situacion_militar: raw.militar || '',
            contacto_emergencia_nombre: raw.contactoEmergenciaNombre || '',
            contacto_emergencia_profesion: raw.contactoEmergenciaProfesion || '',
            contacto_emergencia_celular: raw.contactoEmergenciaCelular || '',
            contacto_referencia_nombre: raw.contactoReferenciaNombre || '',
            contacto_referencia_profesion: raw.contactoReferenciaProfesion || '',
            contacto_referencia_celular: raw.contactoReferenciaCelular || '',
        };

        // Si es conductor, agrego licencias
        if (rol === 'conductor') {
            persona.tipo_licencia = raw.tipoLicencia;
            persona.numero_licencia = raw.numeroLicencia;
            persona.vencimiento_licencia = raw.fechaVencimientoLicencia;
            persona.anio_inicio_conductor = raw.anioInicioConductor;
        };

        return persona;
    };
    const createPayload = () => {
        // 1) Leer del sessionStorage
        const rawPropietario = JSON.parse(sessionStorage.getItem('propietario'));
        const rawConductor = JSON.parse(sessionStorage.getItem('conductor'));
        const rawTenedor = JSON.parse(sessionStorage.getItem('tenedor'));
        const rawVehiculo = JSON.parse(sessionStorage.getItem('vehiculo'));

        // 2) Personas
        const personas = [
            mapPersona(rawPropietario, 'propietario'),
            mapPersona(rawConductor, 'conductor'),
            mapPersona(rawTenedor, 'tenedor')
        ];

        // 3) Armar el objeto vehiculo en snake_case
        const vehiculo = {
            placa: rawVehiculo.placa,
            tipo_vehiculo: rawVehiculo.tipoVehiculo,
            marca: rawVehiculo.marca,
            linea: rawVehiculo.linea,
            anno: Number(rawVehiculo.modeloAnio),
            color: rawVehiculo.color,
            carroceria: rawVehiculo.carroceria,
            nro_ejes: rawVehiculo.numeroEjes,
            combustible: rawVehiculo.tipoCombustible,
            peso_vacio: rawVehiculo.pesoVacio,
            kilometraje: rawVehiculo.kilometraje,
            configuracion: rawVehiculo.configuracion,
            fecha_matricula: rawVehiculo.fechaMatricula,
            foto_matricula_frontal: rawVehiculo.fotoMatriculaFrontal,
            foto_matricula_posterior: rawVehiculo.fotoMatriculaPosterior,
            nro_motor: rawVehiculo.numeroMotor,
            nro_chasis: rawVehiculo.numeroChasis,
            licencia_transito: rawVehiculo.licenciaTransito,
            ciudad_organismo: rawVehiculo.ciudadOrganismoTransito,
            empresas_seguros: rawVehiculo.companiaSoat,
            nro_poliza_soat: rawVehiculo.numeroPolizaSoat,
            fecha_expedicion_soat: rawVehiculo.fechaExpedicionSoat,
            fecha_vencimiento_soat: rawVehiculo.fechaVencimientoSoat,
            nro_revision_tecnicomecanica: rawVehiculo.numeroRevisionTecnicoMecanica,
            fecha_revision_tecnicomecanica: rawVehiculo.fechaVencimientoRevision,
            gps_usuario: rawVehiculo.gpsUsuario || '',
            gps_contrasena: rawVehiculo.gpsContrasena || '',
            gps_url: rawVehiculo.gpsUrl || '',
            leasing_numero_contrato: rawVehiculo.leasingNumeroContrato || '',
            leasing_fecha_inicio: rawVehiculo.leasingFechaInicio || '',
            leasing_fecha_vencimiento: rawVehiculo.leasingFechaVencimiento || '',
            poliza_nit_compania: rawVehiculo.polizaNitCompania || '',
            poliza_numero: rawVehiculo.polizaNumero || '',
            poliza_fecha_vencimiento: rawVehiculo.polizaFechaVencimiento || '',
        };

        const empresa = tenant;
        return { personas, vehiculo, empresa };
    }

    // -------------------------------------------------------------------------
    // 14) submitData: envía FormData con fotos (igual que antes)
    // -------------------------------------------------------------------------
    const submitData = async () => {
        // Guardo datos del paso actual
        sessionStorage.setItem(STORAGE_KEYS[step-1], JSON.stringify(formData[step]));
        
        const { personas, vehiculo, empresa } = createPayload();

        const payload = new FormData();

        const vehForm = formData[4];
        const fotoMatriculaFrontal = vehForm.fotoMatriculaFrontal;
        const fotoMatriculaPosterior = vehForm.fotoMatriculaPosterior;

        // Eliminar campos de archivos del objeto vehiculo antes de serializar
        const vehiculoSinFotos = { ...vehiculo };
        delete vehiculoSinFotos.foto_matricula_frontal;
        delete vehiculoSinFotos.foto_matricula_posterior;

        payload.append('personas', JSON.stringify(personas));
        payload.append('vehiculo', JSON.stringify(vehiculoSinFotos));
        payload.append('empresa', empresa);

        // Anexa las dos imágenes al final
        if (fotoMatriculaFrontal instanceof File) {
            payload.append('foto_matricula_frontal', fotoMatriculaFrontal);
        }
        if (fotoMatriculaPosterior instanceof File) {
            payload.append('foto_matricula_posterior', fotoMatriculaPosterior);
        }

        // Enviar el payload
        try {
            await createVinculacion(payload);
            openModal({ title: 'Vinculación enviada correctamente', mode: 'success' });
        } catch (error) {
            if (error.response?.status === 422) {
                const fieldErrors = error.response.data.errors; 
                const formattedErrors = {};
                for (const [field, messages] of Object.entries(fieldErrors)) {
                    formattedErrors[field] = messages;
                }
                setErrors(formattedErrors);
                toast.error('Por favor corrige los errores antes de continuar', {
                    position: 'top-right'
                });
            } else {
                console.error(error);
                toast.error('Error al enviar la vinculación');
            }
        }
    }

    // -------------------------------------------------------------------------
    // 15) onContinue + handleModalResponse: validaciones y control 
    // -------------------------------------------------------------------------
    const onContinue = () => {
        // a) Elijo el esquema de validación
        const schema = step < 3
            ? terceroBaseSchema
            : step === 3
                ? conductorSchema
                : vehiculoSchema;

        // b) Valido con Zod
        const result = schema.safeParse(formData[step]);
        let fieldErrs = {};
        if (!result.success) {
            fieldErrs = result.error.flatten().fieldErrors;
        }
        // Validación de documento en pasos 1,2,3
        if (step < 4) {
            const docResult = documentoSchema.safeParse(formData[step]);
            if (!docResult.success) {
                const docErrs = docResult.error.flatten().fieldErrors;
                // fusionamos any document field errors
                fieldErrs = { ...fieldErrs, ...docErrs };
            }
        }
        // Validaciones extra de Nombre / Razón social
        if (step < 4) {
            const d = formData[step];
            if (d.naturaleza === 'N') {
                if (!d.primerNombre)   fieldErrs.primerNombre = ['Requerido'];
                if (!d.primerApellido) fieldErrs.primerApellido = ['Requerido'];
            } else {
                if (!d.razonSocial) fieldErrs.razonSocial = ['Requerido'];
            }
        }

        if (Object.keys(fieldErrs).length) {
            setErrors(fieldErrs);
            toast.error('Por favor completa todos los campos requeridos.', {
                position: 'top-right'
            });
            return;
        }
        setErrors({});

        // c) Dependiendo del paso abro el modal correspondiente
        if (step === 1) {
            openModal({
                title: '¿El Propietario y el Tenedor son la misma persona?',
                mode:  'samePerson'
            });
        } else if (step === 2) {
            openModal({
                title: '¿El Tenedor y el Conductor son la misma persona?',
                mode:  'sameTenedorConductor'
            });
        } else if (step === 3) {
            openModal({
                title: '¿Deseas guardar y continuar al siguiente paso?',
                mode: 'confirm'
            });
        } else {
            if (policyUrl) {
                openModal({ 
                    title: '¿Acepta la política de tratamiento de datos?', 
                    mode: 'policy', 
                    url: policyUrl
                });
            } else {
                openModal({ 
                    title: '¿Acepta la política de tratamiento de datos?', 
                    mode: 'policy', 
                    url: null
                });
            }
        }
    };

    // -------------------------------------------------------------------------
    // handleModalResponse: lógica de guardado y avance de pasos
    // -------------------------------------------------------------------------
    const handleModalResponse = async (answer) => {
        const { mode } = modal;

        // Primer modal: Propietario → Tenedor
        if (mode === 'samePerson') {
            saveAndPropagate('propietario', formData[1]);
            if (answer) {
                toast.success('Información del Propietario y Tenedor guardada correctamente');
                // Duplico y guardo Tenedor
                saveAndPropagate('tenedor', formData[1]);
                setFormData(f => ({ ...f, 2: formData[1] }));
                setModal({
                    visible: true,
                    title: '¿El Tenedor y el Conductor son la misma persona?',
                    mode:  'sameTenedorConductor'
                });
            } else {
                toast.success('Información del Propietario guardada correctamente');
                closeModal();
                setStep(2);
            }
            setShowMore(false);
            return;
        }

        // Segundo modal: Tenedor → Conductor
        if (mode === 'sameTenedorConductor') {
            saveAndPropagate('tenedor', formData[2]);

            if (answer) {
                toast.success('Información del Tenedor y Conductor guardada correctamente');
                setFormData(prev => {
                    const tenData = prev[2];
                    const existingCond = prev[3] || {};
                    const condData = {
                        ...tenData,
                        tipoLicencia: existingCond.tipoLicencia ?? tenData.tipoLicencia,
                        numeroLicencia: existingCond.numeroLicencia ?? tenData.numeroLicencia,
                        fechaVencimientoLicencia: existingCond.fechaVencimientoLicencia ?? tenData.fechaVencimientoLicencia,
                        anioInicioConductor: existingCond.anioInicioConductor ?? tenData.anioInicioConductor
                    };
                    try {
                        saveAndPropagate('conductor', condData);
                    } catch (e) {
                        console.error('Error guardando datos de conductor en sessionStorage', e);
                    }
                    return { ...prev, 3: condData };
                });
            } else {
                toast.success('Información del Tenedor guardada correctamente');
            }
            closeModal();
            setStep(3);
            setShowMore(false);
            return;
        }

        // 3) Modal de confirmación para paso 3 
        if (mode === 'confirm') {
            closeModal();
            setShowMore(false);
            if (!answer) return;
            
            saveAndPropagate('conductor', formData[3]);
            const next = JSON.parse(sessionStorage.getItem('vehiculo') || '{}');
            setFormData(f => ({ ...f, 4: next }));
            toast.success('Información del Conductor guardada correctamente');
            setStep(4);
            setShowMore(false);
            setModal(m => ({ ...m, visible: false }));
            return;
        }

        // 4) Modal de política de datos
        if (mode === 'policy') {
            closeModal();
            if (!answer) return; 
            setIsSubmitting(true);
            try {
                await submitData();
            } finally {
                setIsSubmitting(false)
            }
        }

        // 5) Modal de éxito de envío
        if (mode === 'success') {
            closeModal();
            // Limpio sessionStorage y estado
            STORAGE_KEYS.forEach(key => sessionStorage.removeItem(key));
            setFormData({
                1: { naturaleza: 'N', digitoVerificacion: '' },
                2: { digitoVerificacion: '' },
                3: { digitoVerificacion: '' },
                4: {}
            });
            setErrors({});
            setShowMore(false);
            setStep(1);
            return;
        }
    };

    // -------------------------------------------------------------------------
    // Options para el formulario
    // -------------------------------------------------------------------------
    const options = useMemo(() => ({
        generos,
        tiposDocumento,
        naturalezas,
        paises,
        municipios,
        licencias,
        tiposVehiculos,
        empresasSeguros,
        marcas,
        combustibles,
        carrocerias,
        configuraciones,
        lineas
    }),
    [
        generos,
        tiposDocumento,
        naturalezas,
        paises,
        municipios,
        licencias,
        tiposVehiculos,
        empresasSeguros,
        marcas,
        combustibles,
        carrocerias,
        configuraciones,
        lineas
    ]
);

    return {
        step,
        formData,
        errors,
        showMore,
        modal,
        title,
        icon,
        logoUrls,
        logoFallback,
        companyName: nameState,
        options,
        onChange,
        onContinue,
        onBack: handleBack,
        onToggleShowMore,
        handleModalResponse,
        onClose: closeModal,
        loadingForm: empresaLoading,
        error: empresaError,
        isSubmitting
    };
}